import { useEffect, useRef, useState } from "react";
import MessageInfoTableList, { GridCol, GridRow } from "../../components/table/MessageInfoTableList";
import { auth } from "../../firebase";
import { ChevronLeftIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Progress } from "../../components/progress/Progress";
import Button from "../../components/button/Button";
import { useNavigate, useParams } from "react-router-dom";
import { TextField } from "../../components/fields/Fields";
import userQuery from "../../firestore/User.query";
import { ReadMessages, UserData, UserSearchData, UserSearchDefault } from "../../model/Users";
import { EventJoinModal } from "./EventJoinModal";
import messagesQuery from "../../firestore/Messages.query";
import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import companiesQuery from "../../firestore/Companies.query";
import { UserSearchModal } from "./UserSearchModal";
import { ConstMessage } from "../../config/const";

export const UserSearch = () => {
  const navigate = useNavigate();
  const currentUser = auth.currentUser;
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [userResult, setUserResult] = useState<UserSearchData>(UserSearchDefault);
  const [searchFlg, setSearchFlg] = useState(true);
  const [rowList, setRowList] = useState<GridRow[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [addDistributionUser, setAddDistributionUser] = useState(false);
  const [idError, setIdError] = useState("");
  const [kanaError, setKanaError] = useState("");
  const [dialogType, setDialogType] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const { messageId } = useParams<{ messageId: string }>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isText, setIsText] = useState("");
  const addFlg = useRef<boolean>(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (loadProgress >= 100) {
      setLoadProgress(-1);
    }
    setRowList([{ rowData: { id: "", userName: "　", action: null } }]);
  }, [loadProgress]);

  const columns: GridCol[] = [
    { field: "id", headerName: "利用者番号", style: "w-2/6", sort: true },
    { field: "userName", headerName: "名前", style: "w-3/6", sort: false },
    { field: "action", headerName: "", style: "w-1/6", sort: false },
  ];

  const onClickSearch = async () => {
    if (userResult.id.length !== 11 && userResult.id.length > 0) {
      setIdError("11桁で入力してください。");
      return;
    }

    const kanaRegex = /^[ァ-ヶー　]*$/;
    if (userResult.lastNameKana && !kanaRegex.test(userResult.lastNameKana)) {
      setKanaError("氏名カナは全角カナで入力してください。");
      return;
    }

    if (!userResult.id && !userResult.lastName && !userResult.lastNameKana) {
      // 検索項目がすべて未入力の場合
      setIsDialogOpen(!isDialogOpen,);
      setIsText(ConstMessage.ERR_SEARCH_ITEM_INPUT_REQUIRED);
      return;
    }

    // Firestoreから利用停止以外、または利用者番号（完全一致）取得
    let userListDocs = await userQuery.getUserSearch(
      currentUser?.tenantId!,
      userResult.id,
    );

    // 氏名（部分一致）
    if (userResult.lastName != "") {
      userListDocs = userListDocs.filter((userDataNewList: {lastName?: String}) => 
        userDataNewList.lastName?.match(userResult.lastName)
      )
    }
    // フリガナ（部分一致）
    if (userResult.lastNameKana != "") {
      userListDocs = userListDocs.filter((userDataNewList: {lastNameKana?: String}) => 
        userDataNewList.lastNameKana?.match(userResult.lastNameKana!)
      )
    }

    if (userListDocs.length > 0) {
      const rows = userListDocs.map((user) => ({
        rowData: {
          id: user.displayId,
          userName: user.lastName,
          action: (
            <div className="flex justify-end">
              <Button onClick={() => handleJoinClick(user.displayId, user.lastName, user.id)}>
                参加
              </Button>
            </div>
          ),
        },
      }));
      setRowList(rows);
    } else {
      setIsDialogOpen(!isDialogOpen);
      setIsText(ConstMessage.ERR_SEARCH_ITEM_INPUT_REQUIRED1);
    }
  };

  const handleJoinClick = async (id: string, userName: string, userId: string) => {
    const topic = await messagesQuery.getTopic(messageId ?? '');
    let addUser = false;
  
    // グループ配信の場合
    if (topic.length > 0) {
      const company = await companiesQuery.getCompany(currentUser?.tenantId!);
      // 利用者情報を取得
      const user = await userQuery.getUser(currentUser?.tenantId!, userId) as UserData;
      // 利用者のグループ取得
      const userGroupList = await userQuery.getGroups(user.id);
      const displayUserGroupList = userGroupList.filter(
        (group) => group.id !== "all" && group.id !== company.displayId
      );
      // 利用者のグループIDがmessage.topicに存在するか確認
      addUser = displayUserGroupList.some(group => {
        const findTopic = topic.find((t) => t.id === group.id);
        if (findTopic === undefined || findTopic.eventAt === null) return false;
        return !(new Date(group.addDate.toDate()) > findTopic.eventAt);
      });
    } else {
      // 個別の場合
      const tokenList = await messagesQuery.getToken(messageId!);
      // tokenListにselectedUserIdと同じuserIdフィールドが存在するか確認
      addUser = tokenList.some(token => token.userId === userId);
    }
  
    setDialogType("confirm");
    setDialogTitle("参加確認");
    setDialogMessage(addUser ? "以下の利用者をイベントに参加登録します。よろしいですか？" : "以下の利用者はイベントが配信されていません。イベントに参加登録しますか？");
    setSelectedId(id);
    setSelectedUserName(userName);
    setSelectedUserId(userId);
    setDialogOpen(true);
    setAddDistributionUser(addUser);
  };

  const handleJoinWrapper = () => {
    handleJoin(addDistributionUser);
  };
  
  const handleJoin = async (addUser: boolean) => {
    const tenantId = currentUser?.tenantId!;
    const message = await messagesQuery.getMessage(messageId!);
    const topic = await messagesQuery.getTopic(messageId!);
    message.topic = topic.map((t) => t.id);
  
    const readMessages: ReadMessages = {
      userId: selectedUserId,
      messageId: messageId!,
      attendanceAt: new Date(),
      attendanceButtonName: "会場で参加",
      confirmationCheck: true,
      attendanceFlg: true,
      participationFlg: !addUser,
    };
    const result = await userQuery.updateReplyButton(tenantId, readMessages, message.staffConfirmedFlg ?? false, message);
    setDialogOpen(false);
    if (result) {
      addFlg.current = true;
      dispatch(alertOpen(AlertValue(`利用者の出席を確認しました。利用者番号：${selectedId}　利用者名：${selectedUserName}`, "success")));
    } else {
      dispatch(alertOpen(AlertValue("参加時にエラーが発生しました。", "danger")));
    }
  };

  const handleLastNameChange = (e: { target: { value: any } }) => {
    const value = e.target.value;
    setUserResult({ ...userResult, lastName: value });
  };

  const handleKanaChange = (e: { target: { value: any } }) => {
    const value = e.target.value;
    setUserResult({ ...userResult, lastNameKana: value });
    setKanaError("");
  };

  const handleIdChange = (e: { target: { value: any } }) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 11) {
      setUserResult({ ...userResult, id: value });
      if (value.length !== 11 && value.length > 0) {
        setIdError("利用者番号は11桁で入力してください。");
      } else {
        setIdError("");
      }
    }
  };

  const handleClick = () => {
    setSearchFlg(!searchFlg);
  };

  return (
    <div className="h-full">
      <Progress open={loadProgress >= 0 && loadProgress < 100} progressNum={loadProgress} color={"green"} />
      <nav className="flex items-start px-4 py-3" aria-label="Breadcrumb">
        <div className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer" onClick={() => navigate(`/message/${messageId}/attendance`, { state: {addFlg: addFlg.current} })}>
          <ChevronLeftIcon className="-ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
          <span>出欠一覧へ</span>
        </div>
      </nav>
      <div className="px-3 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-bold text-gray-900">
            利用者検索
          </h2>
        </div>
        <div className="mt-3"></div>
        <h3 className="text-sm text-gray-900">　イベントに参加する利用者を選択してください。</h3>
        <div className="mt-3"></div>
      </div>
      <div className="px-6 pb-4">
        <div className="flex flex-col">
          <div className="inline-block min-w-[820px] p-1 align-middle">
            <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full table-fixed divide-y">
                <div className="sm:items-center p-2">
                  <div className="flex justify-between cursor-pointer" onClick={handleClick}>
                    <h2 className="flex font-semibold text-gray-900 items-center">
                      <MagnifyingGlassIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                      検索
                    </h2>
                    {searchFlg ? (
                      <ChevronUpIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                    ) : (
                      <ChevronDownIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                    )}
                  </div>
                  {searchFlg && (
                    <>
                      <div className="flex">
                        <table className="table-fixed">
                          <div className="sm:items-center p-2">
                            <div className="flex items-center">
                              <div className="flex items-center min-w-fit">
                                <h3 className="text-sm text-gray-900 min-w-fit">利用者番号</h3>
                                <TextField
                                  className="h-10 w-44 ml-2"
                                  inputClassName="h-10 w-44"
                                  value={userResult.id}
                                  onChange={(e: any) => {
                                    const value = e.target.value;
                                    handleIdChange(e);
                                    if (value.length <= 11) {
                                      setIdError("");
                                    }
                                  }}
                                  error={!!idError}
                                  helperText={idError}
                                  inputProps={{ maxLength: 11 }}
                                />
                              </div>
                              <div className="flex items-center min-w-fit">
                                <h3 className="text-sm text-gray-900 ml-10 min-w-fit">氏名</h3>
                                <TextField
                                  className="h-10 w-44 ml-2"
                                  inputClassName="h-10 w-44"
                                  value={userResult.lastName}
                                  onChange={handleLastNameChange}
                                />
                              </div>
                              <div className="flex items-center min-w-fit">
                                <h3 className="text-sm text-gray-900 ml-10 min-w-fit">フリガナ</h3>
                                <TextField
                                  className="h-10 w-44 ml-2"
                                  inputClassName="h-10 w-44"
                                  value={userResult.lastNameKana}
                                  onChange={handleKanaChange}
                                  error={!!kanaError}
                                  helperText={kanaError}
                                />
                              </div>
                            </div>
                          </div>
                        </table>
                      </div>
                      <div className={`flex sm:items-center justify-end ${kanaError == "" ? "mt-2" : "mt-6"}`}>
                        <Button className="h-10 w-32 text-sm sm:items-center" onClick={onClickSearch}>
                          検索
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 pb-6">
        <MessageInfoTableList header={false} footer={false} gridCol={columns} gridRow={rowList} />
      </div>
      <UserSearchModal
        children={undefined}
        open={isDialogOpen}
        text={isText}
        toggle={() => {setIsDialogOpen(!isDialogOpen)}}
        closeOnClickOutside={false}
      />
      {/* 参加確認ダイアログ表示 */}
      {dialogOpen && (
        <EventJoinModal
          open={dialogOpen}
          toggle={() => setDialogOpen(!dialogOpen)}
          handleJoin={handleJoinWrapper}
          selectedUserId={selectedId}
          selectedUserName={selectedUserName}
          closeOnClickOutside={false}
          children={undefined}
          type={dialogType}
          title={dialogTitle}
          message={dialogMessage}
        />
      )}
    </div>
  );
};